import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useManualRefresh from "src/hooks/useManualRefresh";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { useDeleteExpense, useGetExpenses } from "../api-clients";
import useExpensesTableColumn from "./useExpensesTableColumn";

const ExpensesList = () => {
  useSetActiveTab(TAB_NAMES.EXPENSES);
  const navigate = useNavigate();
  const [length, setLength] = useState(10);
  const [isCreateExpenseModalOpen, setIsCreateModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);
  const {
    isLoading,
    error,
    data,
    mutate: reloadExpensesData,
    isValidating,
  } = useGetExpenses({ length, page, search: debouncedSearch });
  const { isRefreshing, handleRefresh } = useManualRefresh(reloadExpensesData);

  const {
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSucceed,
    error: deleteErr,
    execute: deleteExpense,
  } = useDeleteExpense();

  const expensesData = useMemo(() => data?.data?.rows ?? [], [data?.data]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const columns = useExpensesTableColumn({
    onDelete: (id: number) => {
      setIdToOperate(id);
      setIsDeleteModalOpen(true);
    },
  });

  useEffect(() => {
    if (isDeleteSucceed) {
      reloadExpensesData();
      setIsDeleteModalOpen(false);
      successNotification("Expense Deleted.");
    }
  }, [isDeleteSucceed, successNotification, reloadExpensesData]);

  useEffect(() => {
    if (deleteErr) {
      errorNotification();
    }
  }, [deleteErr, errorNotification]);

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deleteExpense(idToOperate)}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Expenses"
          onRefresh={() => handleRefresh()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(expensesData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Add Expenses",
            onClick: () => navigate("create"),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={expensesData}
          isLoading={isLoading || isRefreshing}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default ExpensesList;
